import { useMemo, useState } from 'react';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { APP_ROLES } from '@infras/shared-types';

/**
 * Root-only User admin (Settings → Team). Lists EVERYONE the app knows — anyone
 * who's signed in (app_users) plus anyone explicitly role-assigned (app_roles) —
 * and lets root assign a role inline. Users with no assignment show as
 * 'new member' (onboarding access only). Removing an assignment drops the user
 * back to 'new member'; the server refuses to demote/remove the last root.
 */
const USERS = gql`
  query Users {
    users {
      email
      displayName
      role
      assignedBy
      assignedAt
      lastSeenAt
    }
  }
`;

const SET_USER_ROLE = gql`
  mutation SetUserRole($email: String!, $role: String!) {
    setUserRole(email: $email, role: $role) {
      email
      role
    }
  }
`;

const REMOVE_USER_ROLE = gql`
  mutation RemoveUserRole($email: String!) {
    removeUserRole(email: $email)
  }
`;

interface AdminUser {
  email: string;
  displayName?: string | null;
  role: string;
  assignedBy?: string | null;
  assignedAt?: string | null;
  lastSeenAt?: string | null;
}

export function TeamRoles() {
  const { data, loading, error, refetch } = useQuery<{ users: AdminUser[] }>(USERS, {
    fetchPolicy: 'cache-and-network',
  });
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState<string>('engineer');
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [mutErr, setMutErr] = useState<string | null>(null);

  const onDone = () => {
    setMutErr(null);
    void refetch();
  };
  const onErr = (e: Error) => setMutErr(e.message);

  const [setRole, { loading: setting }] = useMutation(SET_USER_ROLE, { onCompleted: onDone, onError: onErr });
  const [removeRole, { loading: removing }] = useMutation(REMOVE_USER_ROLE, { onCompleted: onDone, onError: onErr });

  const all = data?.users ?? [];
  const busy = setting || removing;

  /**
   * Role-count cards (phase 4). The table alone answers "what is this person",
   * never "how is access distributed" — and the second question is the one a root
   * user opens this page with. Clicking a card filters the table to that role, so
   * the summary is a control rather than decoration.
   */
  const counts = useMemo(() => {
    const c: Record<string, number> = { root: 0, maintainer: 0, engineer: 0, new_member: 0 };
    for (const u of all) c[u.role] = (c[u.role] ?? 0) + 1;
    return c;
  }, [all]);
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return all.filter((u) => {
      if (roleFilter && u.role !== roleFilter) return false;
      if (needle && !(u.email.toLowerCase().includes(needle) || (u.displayName ?? '').toLowerCase().includes(needle)))
        return false;
      return true;
    });
  }, [all, q, roleFilter]);

  if (error) return <p className="error">{error.message}</p>;

  return (
    <div>
      <p className="muted text-xs">
        Everyone who has signed in appears here. Roles are <strong>infras-local</strong> (the staff-auth{' '}
        <code>root</code> claim only bootstraps the first root); unassigned users are{' '}
        <code>new member</code> (onboarding access only). Secret visibility: root = L0+L1+L3 · maintainer
        = L1+L3 · engineer = L3 · new member = none.
      </p>
      <div className="glance-row" style={{ margin: '12px 0' }}>
        {(['root', 'maintainer', 'engineer', 'new_member'] as const).map((r) => (
          <button
            key={r}
            className={`glance-card${roleFilter === r ? ' glance-card--on' : ''}`}
            // Toggling: clicking the active card clears the filter, so the summary
            // can't trap you in a filtered view with no obvious way back.
            onClick={() => setRoleFilter(roleFilter === r ? '' : r)}
            title={`Show only ${r.replace('_', ' ')}`}
          >
            <div className="glance-card__n">{counts[r] ?? 0}</div>
            <div className="glance-card__label">{r.replace('_', ' ')}</div>
          </button>
        ))}
      </div>
      {mutErr && <p className="error">{mutErr}</p>}

      <div className="filter-row" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <input placeholder="Filter by email or name…" value={q} onChange={(e) => setQ(e.target.value)} style={{ minWidth: 220 }} />
        <select
          className="class-select"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          title="Filter by role"
        >
          <option value="">All roles</option>
          {APP_ROLES.map((r) => (
            <option key={r} value={r}>{r.replace('_', ' ')}</option>
          ))}
        </select>
        <span className="filter-count">{rows.length} of {all.length}</span>
        <div style={{ flex: 1 }} />
        <input
          placeholder="pre-assign teammate@optisigns.com"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          style={{ minWidth: 240 }}
        />
        <select className="class-select" value={newRole} onChange={(e) => setNewRole(e.target.value)}>
          {APP_ROLES.map((r) => (
            <option key={r} value={r}>{r.replace('_', ' ')}</option>
          ))}
        </select>
        <button
          className="btn btn-primary"
          disabled={busy || !newEmail.trim()}
          onClick={() => {
            void setRole({ variables: { email: newEmail.trim(), role: newRole } });
            setNewEmail('');
          }}
        >
          Assign
        </button>
      </div>

      {loading && !all.length ? (
        <p className="muted">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="muted">{all.length ? 'No users match your filter.' : 'No users yet.'}</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Role</th>
              <th>Last seen</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.email}>
                <td>{u.email}</td>
                <td className="muted">{u.displayName || '—'}</td>
                <td>
                  <select
                    className="class-select class-select--sm"
                    value={u.role}
                    disabled={busy}
                    onChange={(e) => void setRole({ variables: { email: u.email, role: e.target.value } })}
                  >
                    {APP_ROLES.map((role) => (
                      <option key={role} value={role}>{role.replace('_', ' ')}</option>
                    ))}
                  </select>
                </td>
                <td className="muted">{u.lastSeenAt ? new Date(u.lastSeenAt).toLocaleString() : '— not yet —'}</td>
                <td>
                  {u.assignedBy ? (
                    <button
                      className="btn btn-sm btn-danger"
                      disabled={busy}
                      title={`Assigned by ${u.assignedBy}`}
                      onClick={() => {
                        if (window.confirm(`Remove ${u.email}'s role? They fall back to new member.`))
                          void removeRole({ variables: { email: u.email } });
                      }}
                    >
                      Remove
                    </button>
                  ) : (
                    <span className="muted text-xs">default</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
